import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Globe, Save } from 'lucide-react';
import { api, formatApiError } from '../lib/api';
import { invalidateSiteSettingsCache } from '../lib/useSiteSettings';
import { SiteImageAssetSlot } from './SiteImageAssetSlot';
import { DEFAULT_ANPC_HTML, anpcHtmlLooksInvalid, fixAnpcHtml } from '../lib/anpcDefaults';
import { BuiltinAnpcBanners } from './BuiltinAnpcBanners';

const EMPTY_FORM = {
    platform_name: '',
    company_name: '',
    contact_email: '',
    contact_phone: '',
    address: '',
    city: '',
    country: '',
    cui: '',
    trade_register: '',
    footer_text: '',
    anpc_link: 'https://anpc.ro/ce-este-sal/',
    sol_link: 'https://ec.europa.eu/consumers/odr',
    anpc_html: '',
    facebook_url: '',
    instagram_url: '',
    tiktok_url: '',
    linkedin_url: '',
    youtube_url: '',
};

export function PlatformSiteSettings() {
    const [form, setForm] = useState(EMPTY_FORM);
    const [logoUrl, setLogoUrl] = useState(null);
    const [anpcUrl, setAnpcUrl] = useState(null);
    const [paymentBannerUrl, setPaymentBannerUrl] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [logoBusy, setLogoBusy] = useState(false);
    const [anpcBusy, setAnpcBusy] = useState(false);
    const [paymentBusy, setPaymentBusy] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const load = () => {
        setLoading(true);
        api.get('/admin/site-settings')
            .then((r) => {
                const data = r.data || {};
                setForm({
                    platform_name: data.platform_name || '',
                    company_name: data.company_name || '',
                    contact_email: data.contact_email || '',
                    contact_phone: data.contact_phone || '',
                    address: data.address || '',
                    city: data.city || '',
                    country: data.country || '',
                    cui: data.cui || '',
                    trade_register: data.trade_register || '',
                    footer_text: data.footer_text || '',
                    anpc_link: data.anpc_link || 'https://anpc.ro/ce-este-sal/',
                    sol_link: data.sol_link || 'https://ec.europa.eu/consumers/odr',
                    anpc_html: data.anpc_html || '',
                    facebook_url: data.facebook_url || '',
                    instagram_url: data.instagram_url || '',
                    tiktok_url: data.tiktok_url || '',
                    linkedin_url: data.linkedin_url || '',
                    youtube_url: data.youtube_url || '',
                });
                setLogoUrl(data.logo_url || null);
                setAnpcUrl(data.anpc_image_url || null);
                setPaymentBannerUrl(data.payment_banner_url || null);
            })
            .catch((err) => setError(formatApiError(err)))
            .finally(() => setLoading(false));
    };

    useEffect(() => { load(); }, []);

    const set = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));

    const save = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setSaving(true);
        try {
            await api.put('/admin/site-settings', form);
            invalidateSiteSettingsCache();
            setSuccess('Setările site-ului au fost salvate.');
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const uploadAsset = async (kind, file, setUrl, setBusy, urlKey) => {
        setError('');
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            const { data } = await api.post(`/admin/site-settings/${kind}`, fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            setUrl(data[urlKey] || data.url);
            invalidateSiteSettingsCache();
            setSuccess('Imagine actualizată.');
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    const removeAsset = async (kind, setUrl, setBusy) => {
        setError('');
        setBusy(true);
        try {
            await api.delete(`/admin/site-settings/${kind}`);
            setUrl(null);
            invalidateSiteSettingsCache();
            setSuccess('Imagine ștearsă.');
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    if (loading) {
        return <p style={{ color: 'var(--text-muted)' }}>Se încarcă setările site-ului…</p>;
    }

    return (
        <div data-testid="sa-site-settings">
            <div className="admin-panel__toolbar" style={{ marginBottom: '1rem' }}>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.88rem', margin: 0, maxWidth: 620 }}>
                    Logo, date firmă, insigna ANPC și banner metode de plată apar în footer pe homepage și contact — cerințe legale pentru magazine online în România.
                </p>
                <Link to="/" className="btn btn-secondary btn-sm" target="_blank" rel="noreferrer">
                    <Globe size={14} /> Vezi homepage
                </Link>
            </div>

            {error && (
                <div style={{ color: 'var(--neon-magenta)', fontSize: '0.88rem', marginBottom: '0.75rem' }}>{error}</div>
            )}
            {success && (
                <div style={{ color: 'var(--neon-teal)', fontSize: '0.88rem', marginBottom: '0.75rem' }}>{success}</div>
            )}

            <SiteImageAssetSlot
                label="Logo site"
                hint="Afișat în header pe homepage și pagina de contact."
                value={logoUrl}
                busy={logoBusy}
                onUpload={(file) => uploadAsset('logo', file, setLogoUrl, setLogoBusy, 'logo_url')}
                onRemove={() => removeAsset('logo', setLogoUrl, setLogoBusy)}
                testId="sa-site-logo"
                previewHeight={72}
            />

            <SiteImageAssetSlot
                label="Banner metode de plată"
                hint="Logo Visa / Mastercard / Maestro etc. — apare în footer DEASUPRA insignei ANPC, conform cerințelor legale pentru plățile online."
                value={paymentBannerUrl}
                busy={paymentBusy}
                onUpload={(file) => uploadAsset('payment-banner', file, setPaymentBannerUrl, setPaymentBusy, 'payment_banner_url')}
                onRemove={() => removeAsset('payment-banner', setPaymentBannerUrl, setPaymentBusy)}
                testId="sa-site-payment-banner"
                previewHeight={48}
                wide
            />

            <SiteImageAssetSlot
                label="Insignă ANPC (SAL) — imagine"
                hint="Alternativ: lipește codul HTML oficial ANPC mai jos. Dacă HTML-ul e completat, acesta are prioritate față de imagine."
                value={anpcUrl}
                busy={anpcBusy}
                onUpload={(file) => uploadAsset('anpc', file, setAnpcUrl, setAnpcBusy, 'anpc_image_url')}
                onRemove={() => removeAsset('anpc', setAnpcUrl, setAnpcBusy)}
                testId="sa-site-anpc"
                previewHeight={52}
            />

            <div className="panel admin-panel" style={{ marginBottom: '1rem' }}>
                <h3 className="headline admin-panel__title">Cod HTML ANPC (SAL / SOL)</h3>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', margin: '0 0 0.85rem', lineHeight: 1.5 }}>
                    Lipește codul HTML de la ANPC sau folosește codul implicit de mai jos (imagini reale 250×50 px).
                    <strong style={{ color: 'var(--amber)', display: 'block', marginTop: '0.35rem' }}>
                        Nu lăsa texte gen „URL-CATRE-BANNERUL…” — înlocuiește-le cu link-uri reale către imagini.
                    </strong>
                </p>
                {anpcHtmlLooksInvalid(form.anpc_html) && (
                    <div style={{
                        color: 'var(--neon-magenta)', fontSize: '0.85rem', marginBottom: '0.75rem',
                        padding: '0.65rem 0.85rem', borderRadius: 8,
                        background: 'rgba(255,46,146,0.08)', border: '1px solid rgba(255,46,146,0.25)',
                    }} data-testid="sa-site-anpc-html-warning">
                        Codul conține URL-uri placeholder — imaginile nu se vor afișa până nu pui link-uri reale sau apeși „Cod ANPC implicit”.
                    </div>
                )}
                <textarea
                    id="site-anpc-html"
                    className="input"
                    rows={8}
                    value={form.anpc_html}
                    onChange={set('anpc_html')}
                    placeholder={DEFAULT_ANPC_HTML}
                    style={{ fontFamily: 'ui-monospace, monospace', fontSize: '0.82rem', lineHeight: 1.45 }}
                    data-testid="sa-site-anpc-html"
                />
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.65rem' }}>
                    <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => setForm((prev) => ({ ...prev, anpc_html: DEFAULT_ANPC_HTML }))}
                        data-testid="sa-site-anpc-default"
                    >
                        Inserează cod ANPC implicit
                    </button>
                    <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() => setForm((prev) => ({ ...prev, anpc_html: '' }))}
                    >
                        Golește
                    </button>
                </div>
                {form.anpc_html?.trim() && !anpcHtmlLooksInvalid(form.anpc_html) && (
                    <div style={{ marginTop: '1rem' }}>
                        <div className="kicker" style={{ marginBottom: '0.45rem' }}>Previzualizare footer</div>
                        <div className="site-legal-badges site-legal-badges--preview">
                            <div
                                className="site-anpc-html-block"
                                dangerouslySetInnerHTML={{ __html: fixAnpcHtml(form.anpc_html) }}
                            />
                        </div>
                    </div>
                )}
                {!form.anpc_html?.trim() && (
                    <div style={{ marginTop: '1rem' }}>
                        <div className="kicker" style={{ marginBottom: '0.45rem' }}>Implicit în footer (dacă nu pui HTML)</div>
                        <div className="site-legal-badges site-legal-badges--preview">
                            <BuiltinAnpcBanners />
                        </div>
                    </div>
                )}
            </div>

            <form onSubmit={save} className="panel admin-panel">
                <h3 className="headline admin-panel__title">Date firmă & contact</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.85rem' }}>
                    <div>
                        <label className="label" htmlFor="site-platform">Denumire platformă</label>
                        <input id="site-platform" className="input" value={form.platform_name} onChange={set('platform_name')} placeholder="Click Meniu" data-testid="sa-site-platform" />
                        <p style={{ color: 'var(--text-muted)', fontSize: '0.78rem', margin: '0.35rem 0 0' }}>
                            Apare sub logo pe homepage și în footer.
                        </p>
                    </div>
                    <div>
                        <label className="label" htmlFor="site-company">Denumire firmă (legal)</label>
                        <input id="site-company" className="input" value={form.company_name} onChange={set('company_name')} placeholder="Click Meniu SRL" data-testid="sa-site-company" />
                        <p style={{ color: 'var(--text-muted)', fontSize: '0.78rem', margin: '0.35rem 0 0' }}>
                            Apare doar pe pagina Contact, împreună cu CUI și Reg. Com.
                        </p>
                    </div>
                    <div>
                        <label className="label" htmlFor="site-email">Email contact</label>
                        <input id="site-email" className="input" type="email" required value={form.contact_email} onChange={set('contact_email')} placeholder="support@clickmeniu.ro" data-testid="sa-site-email" />
                    </div>
                    <div>
                        <label className="label" htmlFor="site-phone">Telefon</label>
                        <input id="site-phone" className="input" value={form.contact_phone} onChange={set('contact_phone')} placeholder="07xx xxx xxx" data-testid="sa-site-phone" />
                    </div>
                    <div style={{ gridColumn: '1 / -1' }}>
                        <label className="label" htmlFor="site-address">Adresă</label>
                        <input id="site-address" className="input" value={form.address} onChange={set('address')} placeholder="Str. Exemplu nr. 1" data-testid="sa-site-address" />
                    </div>
                    <div>
                        <label className="label" htmlFor="site-city">Oraș</label>
                        <input id="site-city" className="input" value={form.city} onChange={set('city')} placeholder="București" data-testid="sa-site-city" />
                    </div>
                    <div>
                        <label className="label" htmlFor="site-country">Țară</label>
                        <input id="site-country" className="input" value={form.country} onChange={set('country')} placeholder="România" data-testid="sa-site-country" />
                    </div>
                    <div>
                        <label className="label" htmlFor="site-cui">CUI</label>
                        <input id="site-cui" className="input" value={form.cui} onChange={set('cui')} placeholder="RO12345678" data-testid="sa-site-cui" />
                    </div>
                    <div>
                        <label className="label" htmlFor="site-rc">Registrul Comerțului</label>
                        <input id="site-rc" className="input" value={form.trade_register} onChange={set('trade_register')} placeholder="J40/1234/2020" data-testid="sa-site-trade-register" />
                    </div>
                    <div style={{ gridColumn: '1 / -1' }}>
                        <label className="label" htmlFor="site-footer">Text suplimentar footer</label>
                        <input id="site-footer" className="input" value={form.footer_text} onChange={set('footer_text')} placeholder="Servit cu bucurie din România." data-testid="sa-site-footer" />
                        <p className="hint">Copyright-ul (© an + denumire platformă) se generează automat. Aici poți adăuga doar un slogan scurt.</p>
                    </div>

                    {/* Social media — apar ca iconițe în footer-ul site-ului public. Lăsa
                        gol orice rețea pe care n-o folosești; iconița nu va apărea. */}
                    <div style={{ gridColumn: '1 / -1', marginTop: '1.4rem', paddingTop: '1.2rem', borderTop: '1px solid var(--border)' }}>
                        <div className="kicker" style={{ marginBottom: 8 }}>Social media (footer public)</div>
                        <p className="hint" style={{ marginTop: 0, marginBottom: 12 }}>
                            URL complet (cu <code>https://</code>). Lasă gol ce nu folosești — iconița nu se afișează.
                        </p>
                    </div>
                    <div>
                        <label className="label" htmlFor="site-fb">Facebook URL</label>
                        <input id="site-fb" className="input" type="url" value={form.facebook_url}
                               onChange={set('facebook_url')} placeholder="https://facebook.com/clickmeniu"
                               data-testid="sa-site-facebook" />
                    </div>
                    <div>
                        <label className="label" htmlFor="site-ig">Instagram URL</label>
                        <input id="site-ig" className="input" type="url" value={form.instagram_url}
                               onChange={set('instagram_url')} placeholder="https://instagram.com/clickmeniu"
                               data-testid="sa-site-instagram" />
                    </div>
                    <div>
                        <label className="label" htmlFor="site-tt">TikTok URL</label>
                        <input id="site-tt" className="input" type="url" value={form.tiktok_url}
                               onChange={set('tiktok_url')} placeholder="https://tiktok.com/@clickmeniu"
                               data-testid="sa-site-tiktok" />
                    </div>
                    <div>
                        <label className="label" htmlFor="site-li">LinkedIn URL</label>
                        <input id="site-li" className="input" type="url" value={form.linkedin_url}
                               onChange={set('linkedin_url')} placeholder="https://linkedin.com/company/clickmeniu"
                               data-testid="sa-site-linkedin" />
                    </div>
                    <div>
                        <label className="label" htmlFor="site-yt">YouTube URL</label>
                        <input id="site-yt" className="input" type="url" value={form.youtube_url}
                               onChange={set('youtube_url')} placeholder="https://youtube.com/@clickmeniu"
                               data-testid="sa-site-youtube" />
                    </div>
                </div>
                <button type="submit" className="btn btn-primary btn-sm" disabled={saving} style={{ marginTop: '1rem' }} data-testid="sa-site-save">
                    {saving ? <span className="spinner" /> : <Save size={14} />} Salvează setările
                </button>
            </form>
        </div>
    );
}
